// Derived onboarding state. Sequential waterfall (Q-S5-S4-1):
//   profile → pending → dashboard. Каждый шаг enabled только если предыдущий resolved + valid.
//
// Source of truth:
//   - docs/specs/sprint-3/stage-1-contracts.md §2.1 + §6.2 + stage-4-screens.md §3
//   - docs/specs/sprint-4/stage-4-frontend.md §5.3 + stage-1-contracts.md §8.2
//   - docs/specs/sprint-5/stage-4-frontend.md §6.2 + stage-1-contracts.md §6.3/§6.5
//
// 9 взаимоисключающих state'ов после Sprint 5 (Q-S5-S4-4 + RF-S5-S4-4):
//   unauth → verifyPending → consentNeeded → surveyPending → uploadAwaitingReview →
//   uploadConfirmed1 / uploadConfirmedMany / dashboardError / surveyDone

import { useMemo } from 'react';
import { useProfileMe } from '@/api/hooks/profile';
import { useUploadHistory } from '@/api/hooks/uploads';
import { useDashboard } from '@/api/hooks/dashboard';
import { resumeStepFromSurvey, type SurveyStepNumber } from '@/lib/survey-progress';
import { useToken } from './useToken';
import type { ProfileMeResponse, ProfileMeSurvey, UploadListItem, User } from '@/api/types';

export type OnboardingState =
  | { kind: 'unauth' }
  | { kind: 'verifyPending'; user: User }
  | { kind: 'consentNeeded'; user: User; currentVersion: string }
  | { kind: 'surveyPending'; user: User; resumeStep: SurveyStepNumber }
  | { kind: 'uploadAwaitingReview'; user: User; uploadId: number }
  | { kind: 'uploadConfirmed1'; user: User }        // Sprint 5 (D-S5-9 + Q-S5-S4-4)
  | { kind: 'uploadConfirmedMany'; user: User }     // Sprint 5
  | { kind: 'dashboardError'; user: User }          // Sprint 5 (RF-S5-S4-4)
  | { kind: 'surveyDone'; user: User };

export type OnboardingResult = {
  state: OnboardingState | undefined;
  isLoading: boolean;
};

export function useOnboardingState(): OnboardingResult {
  const { token, isLoaded: tokenLoaded } = useToken();
  const hasToken = !!token;
  const profileQuery = useProfileMe(hasToken);

  const baseState = useMemo(() => {
    if (!tokenLoaded) return { kind: 'loading' as const };
    if (!hasToken) return { kind: 'unauth' as const };
    if (profileQuery.isPending) return { kind: 'loading' as const };
    if (profileQuery.isError || !profileQuery.data) return { kind: 'undef' as const };
    return deriveOnboardingState(profileQuery.data);
  }, [tokenLoaded, hasToken, profileQuery.isPending, profileQuery.isError, profileQuery.data]);

  const shouldCheckPending =
    baseState.kind !== 'loading' &&
    baseState.kind !== 'unauth' &&
    baseState.kind !== 'undef' &&
    baseState.kind === 'surveyDone';

  // v1.1 RF-4: явный enabled — не дёргаем GET /uploads?status=awaiting_user_review
  // для unauth/preconsent/preSurvey states (получили бы 401/403).
  const pendingQuery = useUploadHistory(
    { status: 'awaiting_user_review' },
    { enabled: shouldCheckPending },
  );

  // HEAL-46a D-18: дашборд нужен ТЕПЕРЬ И ПРИ непроверенных анализах.
  //
  // Раньше его не запрашивали, когда есть pending: проверка выигрывала приоритет
  // безусловно, и число подтверждённых анализов ни на что не влияло. Теперь
  // влияет — именно им отличается «первая загрузка» (Главной ещё нет, показываем
  // проверку) от «человек с историей» (Главная своя, проверка живёт по строке).
  const shouldCheckDashboard = shouldCheckPending && pendingQuery.isSuccess;
  const dashboardQuery = useDashboard({ enabled: shouldCheckDashboard });

  if (baseState.kind === 'loading') return { state: undefined, isLoading: true };
  if (baseState.kind === 'unauth') return { state: { kind: 'unauth' }, isLoading: false };
  if (baseState.kind === 'undef') return { state: undefined, isLoading: false };

  if (baseState.kind !== 'surveyDone') return { state: baseState, isLoading: false };

  if (pendingQuery.isPending) return { state: undefined, isLoading: true };
  if (pendingQuery.isError) return { state: baseState, isLoading: false };

  // RF-S5-S4-4: 5xx/network на /dashboard НЕ маскируем через surveyDone —
  // юзер с реальными confirmed uploads увидит «загрузите анализ» вместо retry.
  if (dashboardQuery.isPending) return { state: undefined, isLoading: true };
  if (dashboardQuery.isError) {
    return { state: { kind: 'dashboardError', user: baseState.user }, isLoading: false };
  }

  return {
    state: deriveOnboardingStateWithPendingAndConfirmed(
      baseState,
      pendingQuery.data?.data,
      dashboardQuery.data?.analyses_count ?? 0,
    ),
    isLoading: false,
  };
}

function deriveOnboardingState(profile: ProfileMeResponse): OnboardingState {
  const { user, survey, meta } = profile;

  if (user.email_verified_at === null) {
    return { kind: 'verifyPending', user };
  }

  const needConsent =
    user.consent_version === null || user.consent_version !== meta.current_consent_version;
  if (needConsent) {
    return { kind: 'consentNeeded', user, currentVersion: meta.current_consent_version };
  }

  if (!survey.exists || survey.completed === false) {
    return { kind: 'surveyPending', user, resumeStep: computeResumeStep(survey) };
  }

  return { kind: 'surveyDone', user };
}

/**
 * Pure derivation для §7.4 unit-тестов (Sprint 4 carry-forward). Берёт base
 * OnboardingState + список pending uploads (≤1 result expected) и возвращает либо
 * `uploadAwaitingReview` (если pending есть), либо base (passthrough для non-surveyDone
 * states или empty pending list).
 */
export function deriveOnboardingStateWithPending(
  base: OnboardingState,
  pendingList: UploadListItem[] | undefined,
): OnboardingState {
  if (base.kind !== 'surveyDone') return base;
  if (!pendingList || pendingList.length === 0) return base;
  return { kind: 'uploadAwaitingReview', user: base.user, uploadId: pendingList[0].id };
}

/**
 * Sprint 5 pure derivation (T-S5-11). Расширяет Sprint 4 deriveOnboardingStateWithPending —
 * если pending пустой и base.kind === 'surveyDone', смотрит analysesCount → confirmed{1|Many}
 * vs passthrough surveyDone.
 *
 * analysesCount = -1 sentinel: «неизвестно / dashboard ещё не запрошен» — passthrough.
 */
export function deriveOnboardingStateWithPendingAndConfirmed(
  base: OnboardingState,
  pendingList: UploadListItem[] | undefined,
  analysesCount: number,
): OnboardingState {
  if (base.kind !== 'surveyDone') return base;

  // HEAL-46a D-18 — Главная НИКОГДА не подменяется экраном проверки.
  //
  // Раньше любой непроверенный анализ захватывал `/` целиком: пока не подтвердишь —
  // другого экрана нет. На одиночной загрузке это читалось как «доделай начатое».
  // На пакете превращается в ловушку: человек заперт до конца разбора, а выйти
  // некуда — кнопка «На Главную» вела на `/`, где его встречала та же проверка.
  //
  // Сначала здесь было исключение для человека без единого подтверждённого
  // анализа: мол, Главной у него по сути нет, и пустой экран хуже открытой
  // проверки. Живая проверка 21.09 показала обратное — Главная со строкой
  // «2 анализа ждут проверки» читается отлично, а исключение давало ровно ту
  // путаницу, ради избавления от которой всё делалось: человек жал «На Главную»,
  // адрес менялся, а экран оставался прежним.
  //
  // Непроверенные анализы теперь живут только в строке-входе, а путь к ним —
  // явный: экран обработки ведёт на проверку конкретного анализа, экран пакета —
  // на свои файлы.
  if (analysesCount >= 2) return { kind: 'uploadConfirmedMany', user: base.user };
  if (analysesCount === 1) return { kind: 'uploadConfirmed1', user: base.user };

  return base;
}

export function computeResumeStep(survey: ProfileMeSurvey): SurveyStepNumber {
  return resumeStepFromSurvey(survey);
}
